import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { DailyReportInstallationIdentity } from "@/components/reports/DailyReportInstallationIdentity";
import { formatDailyReportHours } from "@shared/dailyReportHours";
import { Calendar, Clock, FileText, Plus, Search, X } from "lucide-react";
import { useLocation, useSearch } from "wouter";

type DailyReportRow = {
  id: number;
  installationId: number;
  userId: number;
  reportDate: string | Date;
  workDescription: string;
  hoursWorked: number;
  signatureUrl?: string | null;
  installationClientId?: string | null;
  installationClientName?: string | null;
  installationAddress?: string | null;
  canonicalCustomerPartnerId?: number | null;
  canonicalCustomerName?: string | null;
  canonicalCustomerCode?: string | null;
  siteName?: string | null;
  installationCode?: string | null;
  authorName?: string | null;
};

type InstallationOption = {
  id: number;
  clientName: string;
  clientId?: string | null;
  address?: string | null;
  workDescription?: string | null;
};

type AuthorOption = {
  id: number;
  name?: string | null;
  email?: string | null;
};

const MANAGER_ROLES = ["admin", "project_manager", "admin_manager"];

function formatDate(value: string | Date) {
  return new Date(value).toLocaleDateString("es-ES", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function DailyReports() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const canFilterAuthor = MANAGER_ROLES.includes(user?.role || "");
  const authorIdParam = searchParams.get("authorId") || searchParams.get("technicianId");

  const filters = {
    fromDate: searchParams.get("from") || undefined,
    toDate: searchParams.get("to") || undefined,
    installationId: searchParams.get("installationId") ? Number(searchParams.get("installationId")) : undefined,
    client: searchParams.get("client") || undefined,
    authorId: canFilterAuthor && authorIdParam
      ? Number(authorIdParam)
      : undefined,
  };

  const reportsQuery = trpc.dailyReports.list.useQuery(filters);
  const installationsQuery = trpc.installations.list.useQuery();
  const authorsQuery = trpc.users.list.useQuery(undefined, {
    enabled: canFilterAuthor,
  });

  function updateFilter(key: string, value?: string) {
    const next = new URLSearchParams(searchString);
    if (key === "authorId") next.delete("technicianId");
    if (value && value !== "all") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    const nextSearch = next.toString();
    setLocation(nextSearch ? `/daily-reports?${nextSearch}` : "/daily-reports");
  }

  function clearFilters() {
    setLocation("/daily-reports");
  }

  const reports = reportsQuery.data || [];
  const hasFilters = Boolean(
    filters.fromDate ||
    filters.toDate ||
    filters.installationId ||
    filters.client ||
    filters.authorId
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Partes Diarios</h1>
            <p className="mt-2 text-muted-foreground">
              Registro de trabajo diario en las instalaciones
            </p>
          </div>
          <Button onClick={() => setLocation("/daily-reports/new")} className="w-full md:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Parte Diario
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Search className="h-5 w-5" />
              Filtros
            </CardTitle>
            <CardDescription>Busca partes por fecha, obra, cliente o autor del parte. Las horas indicadas son informativas; no individuales ni de nómina.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-5">
              <div className="min-w-0 space-y-1">
                <label className="block break-words text-sm font-medium text-foreground">Desde</label>
                <Input
                  type="date"
                  className="w-full min-w-0 max-w-full"
                  value={filters.fromDate || ""}
                  onChange={(event) => updateFilter("from", event.target.value)}
                />
              </div>
              <div className="min-w-0 space-y-1">
                <label className="block break-words text-sm font-medium text-foreground">Hasta</label>
                <Input
                  type="date"
                  className="w-full min-w-0 max-w-full"
                  value={filters.toDate || ""}
                  onChange={(event) => updateFilter("to", event.target.value)}
                />
              </div>
              <div className="min-w-0 space-y-1">
                <label className="block break-words text-sm font-medium text-foreground">Obra / instalación</label>
                <Select
                  value={filters.installationId ? String(filters.installationId) : "all"}
                  onValueChange={(value) => updateFilter("installationId", value)}
                >
                  <SelectTrigger className="w-full min-w-0 max-w-full">
                    <SelectValue className="min-w-0 flex-1 truncate" placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {(installationsQuery.data || []).map((installation: InstallationOption) => (
                      <SelectItem key={installation.id} value={String(installation.id)}>
                        {installation.clientName} · #{installation.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0 space-y-1">
                <label className="block break-words text-sm font-medium text-foreground">Cliente</label>
                <Input
                  value={filters.client || ""}
                  onChange={(event) => updateFilter("client", event.target.value)}
                  placeholder="Nombre o expediente"
                />
              </div>
              {canFilterAuthor && (
                <div className="min-w-0 space-y-1">
                  <label className="block break-words text-sm font-medium text-foreground">Autor del parte</label>
                  <Select
                    value={filters.authorId ? String(filters.authorId) : "all"}
                    onValueChange={(value) => updateFilter("authorId", value)}
                  >
                    <SelectTrigger className="w-full min-w-0 max-w-full">
                      <SelectValue className="min-w-0 flex-1 truncate" placeholder="Todos los autores" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los autores</SelectItem>
                      {(authorsQuery.data || []).map((author: AuthorOption) => (
                        <SelectItem key={author.id} value={String(author.id)}>
                          {author.name || author.email || `Usuario #${author.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {hasFilters && (
              <div className="mt-4">
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Limpiar filtros
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {reportsQuery.isLoading ? (
          <div className="py-12 text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Cargando partes diarios...</p>
          </div>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium text-foreground">
                {hasFilters ? "No hay partes con esos filtros" : "No hay partes diarios registrados"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Ajusta los filtros o crea un nuevo parte diario.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((report: DailyReportRow) => (
              <button
                key={report.id}
                type="button"
                className="w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-accent/50"
                onClick={() => setLocation(`/daily-reports/${report.id}`)}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-foreground">{formatDate(report.reportDate)}</span>
                      {report.signatureUrl && (
                        <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          Firmado
                        </span>
                      )}
                    </div>
                    <DailyReportInstallationIdentity className="text-sm text-foreground" installation={{
                      id: report.installationId,
                      clientName: report.installationClientName,
                      clientId: report.installationClientId,
                      canonicalCustomerPartnerId: report.canonicalCustomerPartnerId,
                      canonicalCustomerName: report.canonicalCustomerName,
                      canonicalCustomerCode: report.canonicalCustomerCode,
                      siteName: report.siteName,
                      installationCode: report.installationCode,
                    }} />
                    <p className="line-clamp-2 text-sm text-muted-foreground">{report.workDescription}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-3 text-sm text-muted-foreground md:justify-end">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Horas indicadas en el parte: {formatDailyReportHours(report.hoursWorked)} h
                    </span>
                    <span>Autor del parte: {report.authorName || "Sin identificar"}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
